// ─── POI Service (Unified Points of Interest) ───────────────────────────
// Single service for ALL POI types: heritage-landmark, hidden-gem, castle,
// natural-landmark. Reads from the `cities` table where `poi_type IS NOT NULL`.
// This service is READ-ONLY — POI classification is editorial (set poi_type directly in Supabase).

import { supabase } from "./supabaseClient";
import { haversineKm } from "./geoUtils";
import { resolveCity } from "./cityStore";
import { reportError } from "./errorService";
import { POI_COLORS, iconElement } from "../data/design-tokens";
import type { City } from "../data/types";
import type { POIType } from "../data/database.types";

// ─── Types ──────────────────────────────────────────────────────────────

/** A Point of Interest — a City with guaranteed poiType + optional distance. */
export type POI = City & { distanceKm?: number };

/** Result of checking a city's POI status */
export interface POICheck {
  poi_type: POIType | null;
  poi_name: string | null;
  reason: string;
}

// ─── Select columns for POI queries ─────────────────────────────────────

const POI_COLS =
  "city_key, canonical_name, country, country_code, population, latitude, longitude, poi_type, poi_name, poi_description, description, local_tip, wikipedia_url, image_url, showcase_text, spotify_uri";

// ─── Shared row → POI mapper ────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToPOI(row: any): POI | null {
  if (!row.latitude || !row.longitude || !row.poi_type) return null;
  return {
    id: row.city_key,
    name: row.canonical_name,
    city: row.canonical_name,
    country: row.country ?? "",
    population: row.population ?? 0,
    latitude: row.latitude,
    longitude: row.longitude,
    poiType: row.poi_type as POIType,
    poiName: row.poi_name ?? null,
    poiDescription: row.poi_description ?? null,
    imageUrl: row.image_url ?? null,
    poiShowcaseText: row.showcase_text ?? null,
    poiReason: row.poi_description ?? null,
    spotifyUri: row.spotify_uri ?? null,
    description: row.description ?? null,
  };
}

// ─── In-memory session cache ────────────────────────────────────────────

const SESSION_CACHE = new Map<string, POICheck>();
const PENDING = new Map<string, Promise<POICheck>>();

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Check whether a city has a POI designation.
 * Resolves city name → canonical city_key, then reads poi_type from DB.
 * The DB is the single source of truth — no population gates here.
 */
export async function checkPOI(
  cityName: string,
): Promise<POICheck> {
  const cleaned = cityName.toLowerCase();

  if (SESSION_CACHE.has(cleaned)) return SESSION_CACHE.get(cleaned)!;
  if (PENDING.has(cleaned)) return PENDING.get(cleaned)!;

  const request = (async (): Promise<POICheck> => {
    try {
      const city = await resolveCity(cityName);
      if (city) {
        const result: POICheck = {
          poi_type: (city.poiType as POIType) ?? null,
          poi_name: city.poiName ?? null,
          reason: city.poiDescription ?? "",
        };
        SESSION_CACHE.set(cleaned, result);
        return result;
      }

      const result: POICheck = { poi_type: null, poi_name: null, reason: "City not in DB" };
      SESSION_CACHE.set(cleaned, result);
      return result;
    } catch (err) {
      reportError(err, { level: "info", context: { op: "checkPOI", cityName } });
      return { poi_type: null, poi_name: null, reason: "Error" };
    } finally {
      PENDING.delete(cleaned);
    }
  })();

  PENDING.set(cleaned, request);
  return request;
}

/**
 * Sync cache lookup — returns cached POI check, or undefined if not yet fetched.
 */
export function getPOICached(cityName: string): POICheck | undefined {
  const cleaned = cityName.toLowerCase();
  return SESSION_CACHE.get(cleaned);
}


// ─── Nearby POIs ────────────────────────────────────────────────────────

/**
 * Query the cities table for all POIs within `radiusKm` of given coords.
 * Returns up to `maxResults` results sorted by distance ascending.
 */
export async function getNearbyPOIs(
  lat: number,
  lng: number,
  radiusKm = 200,
  maxResults = 8
): Promise<POI[]> {

  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));

  try {
    const { data, error } = await supabase
      .from("cities")
      .select(POI_COLS)
      .not("poi_type", "is", null)
      .gte("latitude", lat - latDelta)
      .lte("latitude", lat + latDelta)
      .gte("longitude", lng - lngDelta)
      .lte("longitude", lng + lngDelta);

    if (error || !data?.length) {
      if (error) reportError(error, { level: "info", context: { op: "getNearbyPOIs" } });
      return [];
    }

    return data
      .map((row) => {
        const poi = rowToPOI(row);
        if (!poi) return null;
        poi.distanceKm = haversineKm(lat, lng, poi.latitude, poi.longitude);
        return poi;
      })
      .filter((poi): poi is POI => !!poi && poi.distanceKm! <= radiusKm && poi.distanceKm! > 2)
      .sort((a, b) => a.distanceKm! - b.distanceKm!)
      .slice(0, maxResults);
  } catch (err) {
    reportError(err, { level: "info", context: { op: "getNearbyPOIs" } });
    return [];
  }
}

/**
 * Fetch ALL POIs (for explore mode). Returns every city with poi_type set.
 * Typically ~400 rows — safe to fetch in one pass.
 */
export async function getAllPOIs(): Promise<POI[]> {

  try {
    const { data, error } = await supabase
      .from("cities")
      .select(POI_COLS)
      .not("poi_type", "is", null);

    if (error || !data?.length) {
      if (error) reportError(error, { level: "info", context: { op: "getAllPOIs" } });
      return [];
    }

    return data
      .map((row) => rowToPOI(row))
      .filter((poi): poi is POI => !!poi);
  } catch (err) {
    reportError(err, { level: "info", context: { op: "getAllPOIs" } });
    return [];
  }
}

// ─── POI Color + Icon Config ────────────────────────────────────────────

export const POI_CONFIG: Record<POIType, { color: string; lineColor: string; symbol: string; label: string }> = {
  "heritage-landmark": { color: POI_COLORS["heritage-landmark"], lineColor: POI_COLORS["heritage-landmark"], symbol: iconElement("heritage-landmark", "svg"), label: "Heritage" },
  "hidden-gem":        { color: POI_COLORS["hidden-gem"],        lineColor: POI_COLORS["hidden-gem"],        symbol: iconElement("hidden-gem", "svg"),        label: "Hidden Gem" },
  "castle":            { color: POI_COLORS["castle"],            lineColor: POI_COLORS["castle"],            symbol: iconElement("castle", "svg"),            label: "Castle" },
  "natural-landmark":  { color: POI_COLORS["natural-landmark"],  lineColor: POI_COLORS["natural-landmark"],  symbol: iconElement("natural-landmark", "svg"),  label: "Landmark" },
};


